/* ============================================================
   VELORA — product page interactions
   ------------------------------------------------------------
   Everything here is written to survive Shopify Theme Editor
   section/block re-rendering:

   * All behaviour is initialised per-root via init(root).
   * Document-level listeners are attached once and delegate, so
     they keep working on nodes injected after page load.
   * shopify:section:load / :unload / :select / :block:select
     re-run initialisation for the affected section only.

   Cart submission itself is NOT handled here — the main product
   section uses Horizon's native <product-form-component>, so
   add-to-cart, cart drawer, error handling and analytics all go
   through the theme's own code path.
   ============================================================ */
(function () {
  'use strict';

  var docReady = false;

  /* ---------------------------------------------------------
     Scroll reveal
     --------------------------------------------------------- */
  function initReveal(root) {
    var reveals = root.querySelectorAll('.v-reveal');
    if (!reveals.length) return;

    if (!('IntersectionObserver' in window)) return;

    document.documentElement.classList.add('v-js-anim');

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('v-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    Array.prototype.forEach.call(reveals, function (el) {
      if (el.classList.contains('v-in')) return;
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.95) {
        el.classList.add('v-in');
      } else {
        io.observe(el);
      }
    });

    // Safety net: never leave content permanently invisible.
    setTimeout(function () {
      Array.prototype.forEach.call(reveals, function (el) {
        el.classList.add('v-in');
      });
    }, 2000);
  }

  /* ---------------------------------------------------------
     Main product: variants, quantity tiers, subscription
     --------------------------------------------------------- */
  function initProduct(root) {
    var form = root.querySelector('[data-velora-product]');
    if (!form) return;

    var dataEl = root.querySelector('[data-velora-pricing]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    var variantInput = root.querySelector('[data-velora-variant-input]');
    var qtyInput = root.querySelector('[data-velora-quantity-input]');
    var planInput = root.querySelector('[data-velora-selling-plan-input]');
    var atcButton = root.querySelector('[data-velora-atc]');
    var optionInputs = root.querySelectorAll('[data-velora-option]');
    var tierInputs = root.querySelectorAll('[data-velora-tier]');
    var subToggle = root.querySelector('[data-velora-subscribe]');
    var gallery = root.querySelector('[data-velora-gallery]');

    function currentVariant() {
      if (!optionInputs.length) return data.variants[0];

      var chosen = [];
      Array.prototype.forEach.call(optionInputs, function (input) {
        if (input.checked) chosen[Number(input.dataset.veloraOption)] = input.value;
      });

      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        var match = true;
        for (var j = 0; j < chosen.length; j++) {
          if (chosen[j] !== undefined && v.options[j] !== chosen[j]) {
            match = false;
            break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    function currentTier() {
      for (var i = 0; i < tierInputs.length; i++) {
        if (tierInputs[i].checked) return tierInputs[i];
      }
      return tierInputs[0] || null;
    }

    function isSubscribing() {
      return !!(subToggle && subToggle.checked);
    }

    function setText(selector, value) {
      var els = root.querySelectorAll(selector);
      Array.prototype.forEach.call(els, function (el) {
        if (value === null || value === undefined || value === '') {
          el.hidden = true;
        } else {
          el.hidden = false;
          el.innerHTML = value;
        }
      });
    }

    function priceEntry(variant, tierId, sub) {
      if (!variant) return null;
      var byVariant = data.prices[String(variant.id)];
      if (!byVariant) return null;
      var mode = sub && byVariant.subscription ? byVariant.subscription : byVariant.onetime;
      if (!mode) return null;
      return mode[tierId] || null;
    }

    function update() {
      var variant = currentVariant();
      var tier = currentTier();
      var tierId = tier ? tier.dataset.veloraTier : 'default';
      var qty = tier ? Number(tier.dataset.veloraQuantity) || 1 : 1;
      var sub = isSubscribing();

      /* --- keep the real Shopify form inputs in sync --- */
      if (variantInput) variantInput.value = variant ? variant.id : '';
      // Only drive the quantity input from a bundle tier. Without tiers the
      // shopper controls quantity directly and we must not overwrite it.
      if (qtyInput && tier) qtyInput.value = qty;
      if (planInput) planInput.value = sub ? planInput.dataset.veloraPlanId || '' : '';

      /* --- per-tier price labels --- */
      Array.prototype.forEach.call(tierInputs, function (input) {
        var entry = priceEntry(variant, input.dataset.veloraTier, sub);
        var wrap = input.closest('.v-tier');
        if (!wrap || !entry) return;
        var now = wrap.querySelector('[data-velora-tier-price]');
        var was = wrap.querySelector('[data-velora-tier-compare]');
        var per = wrap.querySelector('[data-velora-tier-unit]');
        if (now) now.innerHTML = entry.total;
        if (was) {
          was.innerHTML = entry.compare || '';
          was.hidden = !entry.compare;
        }
        if (per && entry.unit) per.innerHTML = per.dataset.veloraTierUnit.replace('[price]', entry.unit);
      });

      /* --- headline price --- */
      var headline = priceEntry(variant, tierId, false);
      if (headline) {
        setText('[data-velora-price]', headline.unit_price || headline.total);
        var compareEls = root.querySelectorAll('[data-velora-compare]');
        Array.prototype.forEach.call(compareEls, function (el) {
          el.innerHTML = headline.unit_compare || '';
          el.hidden = !headline.unit_compare;
        });
        var saleBadge = root.querySelector('[data-velora-sale-badge]');
        if (saleBadge) saleBadge.hidden = !headline.unit_compare;
      }

      /* --- running total --- */
      var entry = priceEntry(variant, tierId, sub);
      if (entry) {
        setText('[data-velora-total]', entry.total);
        var totalCompare = root.querySelector('[data-velora-total-compare]');
        if (totalCompare) {
          totalCompare.innerHTML = entry.compare || '';
          totalCompare.hidden = !entry.compare;
        }
      }

      var note = root.querySelector('[data-velora-total-note]');
      if (note) {
        var noteText = sub ? note.dataset.veloraNoteSubscribe : note.dataset.veloraNoteOnetime;
        note.innerHTML = noteText || '';
        note.hidden = !noteText;
      }

      /* --- availability --- */
      if (atcButton) {
        var available = variant && variant.available;
        atcButton.disabled = !available;
        var label = atcButton.querySelector('[data-velora-atc-label]');
        if (label) {
          label.textContent = !variant
            ? atcButton.dataset.veloraUnavailable
            : available
            ? atcButton.dataset.veloraAdd
            : atcButton.dataset.veloraSoldOut;
        }
      }

      /* --- mark unavailable option values --- */
      Array.prototype.forEach.call(optionInputs, function (input) {
        var idx = Number(input.dataset.veloraOption);
        var possible = data.variants.some(function (v) {
          return v.options[idx] === input.value && v.available;
        });
        var label = input.closest('label');
        if (label) label.classList.toggle('v-opt-unavailable', !possible);
      });

      /* --- media --- */
      if (gallery && variant && variant.featured_media_id) {
        selectMedia(gallery, String(variant.featured_media_id));
      }

      /* --- let the sticky bar mirror the state --- */
      document.dispatchEvent(
        new CustomEvent('velora:selection', {
          bubbles: true,
          detail: {
            total: entry ? entry.total : null,
            compare: entry ? entry.compare : null,
            available: !!(variant && variant.available)
          }
        })
      );
    }

    Array.prototype.forEach.call(optionInputs, function (i) {
      i.addEventListener('change', update);
    });
    Array.prototype.forEach.call(tierInputs, function (i) {
      i.addEventListener('change', update);
    });
    if (subToggle) subToggle.addEventListener('change', update);

    update();
  }

  /* ---------------------------------------------------------
     Media gallery
     --------------------------------------------------------- */
  function selectMedia(gallery, mediaId) {
    var slides = gallery.querySelectorAll('[data-velora-media-id]');
    var thumbs = gallery.querySelectorAll('[data-velora-thumb]');
    var found = false;

    Array.prototype.forEach.call(slides, function (slide) {
      var isMatch = slide.dataset.veloraMediaId === mediaId;
      if (isMatch) found = true;
      slide.hidden = !isMatch;
    });

    if (!found) {
      if (slides[0]) slides[0].hidden = false;
      return;
    }

    Array.prototype.forEach.call(thumbs, function (thumb) {
      thumb.setAttribute('aria-current', thumb.dataset.veloraThumb === mediaId ? 'true' : 'false');
    });
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-velora-gallery]');
    if (!gallery) return;

    gallery.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-velora-thumb]');
      if (!thumb) return;
      event.preventDefault();
      selectMedia(gallery, thumb.dataset.veloraThumb);
    });
  }

  /* ---------------------------------------------------------
     Sticky add to cart bar
     --------------------------------------------------------- */
  function initSticky(root) {
    var bar = root.querySelector('[data-velora-sticky]');
    if (!bar) return;

    var threshold = Number(bar.dataset.veloraStickyAfter) || 600;

    function onScroll() {
      var offerVisible = false;
      var offer = document.querySelector('[data-velora-offer]');
      if (offer) {
        var rect = offer.getBoundingClientRect();
        offerVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      }
      bar.classList.toggle('v-show', (window.scrollY || 0) > threshold && !offerVisible);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    document.addEventListener('velora:selection', function (event) {
      var priceEl = bar.querySelector('[data-velora-sticky-price]');
      if (priceEl && event.detail.total) priceEl.innerHTML = event.detail.total;
      var compareEl = bar.querySelector('[data-velora-sticky-compare]');
      if (compareEl) {
        compareEl.innerHTML = event.detail.compare || '';
        compareEl.hidden = !event.detail.compare;
      }
    });

    /* countdown — only runs when the merchant has enabled it */
    var cd = bar.querySelector('[data-velora-countdown]');
    if (cd) {
      var minutes = Number(cd.dataset.veloraCountdown) || 10;
      var key = 'velora_offer_deadline';
      var deadline = Number(window.sessionStorage ? sessionStorage.getItem(key) : 0);
      if (!deadline || deadline < Date.now()) {
        deadline = Date.now() + minutes * 60 * 1000;
        try {
          sessionStorage.setItem(key, String(deadline));
        } catch (e) {
          /* private mode — countdown simply restarts */
        }
      }
      var tick = function () {
        var s = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
        cd.textContent = Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
      };
      tick();
      if (bar.veloraTimer) clearInterval(bar.veloraTimer);
      bar.veloraTimer = setInterval(tick, 1000);
    }
  }

  /* ---------------------------------------------------------
     Delegated document-level actions (survive re-render)
     --------------------------------------------------------- */
  function initDelegates() {
    document.addEventListener('click', function (event) {
      /* scroll to the buy box */
      var toOffer = event.target.closest('[data-velora-scroll-to-offer]');
      if (toOffer) {
        var offer = document.querySelector('[data-velora-offer]');
        if (offer) {
          event.preventDefault();
          var top = offer.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
        return;
      }

      /* sticky bar add-to-cart: reuse the real product form's button */
      var proxy = event.target.closest('[data-velora-atc-proxy]');
      if (proxy) {
        var real = document.querySelector('[data-velora-atc]');
        if (real && !real.disabled) {
          event.preventDefault();
          real.click();
        }
      }
    });
  }

  /* ---------------------------------------------------------
     Bootstrap
     --------------------------------------------------------- */
  function init(root) {
    if (!root || !root.querySelectorAll) return;
    initReveal(root);
    initGallery(root);
    initProduct(root);
    initSticky(root);
  }

  function boot() {
    if (docReady) return;
    docReady = true;
    initDelegates();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor lifecycle — re-initialise only what changed. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var bar = event.target.querySelector('[data-velora-sticky]');
    if (bar && bar.veloraTimer) clearInterval(bar.veloraTimer);
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var section = event.target.closest('.shopify-section');
    if (section) init(section);
  });
})();
